import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  HostBinding,
  Input,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormGroup,
} from '@angular/forms';

import { Subject, debounceTime, fromEvent, take } from 'rxjs';
import { distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';

import { MwFormBehaviorService, MwFormDataService } from './@services';
import { MwFormUIService } from './@services/form-ui.service';
import { MwFormActionsComponent } from './form-actions';

@Component({
  selector: 'mw-form',
  templateUrl: './form.component.html',
  styleUrls: ['./form.component.scss'],
  providers: [MwFormDataService, MwFormBehaviorService, MwFormUIService],
})
export class MwFormComponent implements AfterViewInit, OnDestroy {
  @ContentChild(MwFormActionsComponent) formActions: MwFormActionsComponent;
  @ViewChild('rootContainer') rootContainer: ElementRef;
  @ViewChild('formContainer') formContainer: ElementRef;
  @ViewChild('stickyBarContainer') stickyBarContainer: ElementRef;

  @Input() formTitle: string;
  @Input() formSubTitle: string;
  @Input() showSidebar: 'left' | 'right' | 'none' = 'none';
  @Input() sectionDividerWidth = 0;
  @Input() form: AbstractControl;
  @Input() autoScrollToInvalidField = false;
  @Input()
  public get stickyActionBar(): boolean {
    return this._stickyActionBar;
  }
  public set stickyActionBar(value: boolean) {
    this._stickyActionBar = value;
    this.setStickyBar();
  }

  @HostBinding('class.mw-form__sections-with-dividers')
  get sectionDividerExist(): boolean {
    return this.sectionDividerWidth > 0;
  }

  private _stickyActionBar: boolean;
  private readonly _unsubscribeAll$ = new Subject<void>();

  constructor(
    private readonly uiService: MwFormUIService,
    private readonly changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnDestroy(): void {
    this._unsubscribeAll$.next();
    this._unsubscribeAll$.complete();
  }

  ngAfterViewInit(): void {
    this.uiService.setRootContainer(this.rootContainer.nativeElement);
    this.uiService.setFormContainer(this.formContainer.nativeElement);

    if (this.form && this.autoScrollToInvalidField) {
      this.form.statusChanges
        .pipe(
          takeUntil(this._unsubscribeAll$),
          debounceTime(500),
          filter((status) => status === 'INVALID')
        )
        .subscribe(() => this.scrollToFirstInvalidControl());
    }
    this.setStickyBar();
    this.changeDetectorRef.detectChanges();
  }

  scrollToFirstInvalidControl(): void {
    const firstInvalidControl: HTMLElement =
      this.rootContainer.nativeElement.querySelector('.ng-invalid');

    if (!firstInvalidControl) {
      return;
    }

    this.rootContainer.nativeElement.scroll({
      top: this.getTopOffset(firstInvalidControl),
      left: 0,
      behavior: 'smooth',
    });

    fromEvent(this.rootContainer.nativeElement, 'scroll')
      .pipe(debounceTime(100), take(1))
      .subscribe(() => firstInvalidControl.focus());
  }

  private updateValueAndValidityAll(control?: AbstractControl): void {
    if (control instanceof UntypedFormArray) {
      control.controls.forEach((item) => this.updateValueAndValidityAll(item));
    }

    if (control instanceof UntypedFormGroup) {
      Object.values(control.controls).forEach((item) => {
        this.updateValueAndValidityAll(item);
      });
    }

    if (control) {
      control.updateValueAndValidity();
    }
  }

  private getTopOffset(controlEl: HTMLElement): number {
    const labelOffset = 50;
    const controlElTop = controlEl.getBoundingClientRect().top;

    const containerTop =
      this.rootContainer.nativeElement.getBoundingClientRect().top;
    const absoluteControlElTop =
      controlElTop + this.rootContainer.nativeElement.scrollTop;

    return absoluteControlElTop - containerTop - labelOffset;
  }

  private setStickyBar(): void {
    setTimeout(() => {
      this.uiService.setStickyBarContainer(
        this.stickyBarContainer?.nativeElement
      );
    });
  }
}
